import {createDialogOverlay, fillDialog, pvpButton} from "../DialogHelpers.js";
import {hostDialogAvailable, requestHostToolPrompt} from "../HostDialog.js";

import {t} from "../i18n/index.js";
// Modal popup asking for the two settings "Tværprofiler" needs right after
// the user finishes digitizing the path (task section 3): profile interval
// and profile length. Same dark-modal/Promise shape as WallThicknessPrompt.js/
// promptFixedAngle() (VectorEditingMenu.js) - deliberately not a third,
// slightly-different popup implementation.
//
// Resolves to:
//   {interval, profileLength} - both positive numbers, entered by the user
//   undefined                 - the user cancelled (Annuller / ESC / tool
//                                switched away) - the caller must not create
//                                any feature/profiles in that case
export function promptCrossSectionSettings(dm, {defaultInterval = 5, defaultProfileLength = 10} = {}){
	// Vært-UI (PointViews frontend) viser dialogen og blokerer "Generér profiler", til begge felter er positive tal;
	// uden vært (standalone Potree) bruges DOM-dialogen herunder.
	if(hostDialogAvailable(dm.viewer)){
		return requestHostToolPrompt(dm.viewer, {
			kind: 'form',
			size: 's',
			title: t('Tværprofilrapport - indstillinger'),
			text: t('Angiv afstanden mellem profilerne langs forløbet, og hvor langt hvert profil skal strække sig på tværs.'),
			fields: [
				{id: 'interval', type: 'number', label: t('Profilinterval [m]'), value: String(defaultInterval), step: 'any', min: 0, minExclusive: true},
				{id: 'profileLength', type: 'number', label: t('Profillængde [m]'), value: String(defaultProfileLength), step: 'any', min: 0, minExclusive: true},
			],
			invalidMessage: t('Angiv to positive tal.'),
			confirmLabel: t('Generér profiler'),
		}).then((result) => {
			if(result.action !== 'confirm') return undefined;
			const interval = parseFloat(result.values.interval);
			const profileLength = parseFloat(result.values.profileLength);
			return (isFinite(interval) && interval > 0 && isFinite(profileLength) && profileLength > 0) ? {interval, profileLength} : undefined;
		});
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 300);
		fillDialog(box, {
			title: t(`Tværprofilrapport - indstillinger`),
			description: t(`Angiv afstanden mellem profilerne langs forløbet, og hvor langt hvert profil skal strække sig på tværs.`),
			body: `<label class="pvp-label">${t("Profilinterval [m]")}</label>
			<input class="pvp-input" data-field="interval" type="number" step="any" min="0.01" value="${defaultInterval}"
			 />
			<label class="pvp-label">${t("Profillængde [m]")}</label>
			<input class="pvp-input" data-field="length" type="number" step="any" min="0.01" value="${defaultProfileLength}"
			 />
			<div class="pvp-error" data-role="error"></div>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Generér profiler"), 'primary'),
		});

		const intervalInput = box.querySelector('[data-field="interval"]');
		const lengthInput = box.querySelector('[data-field="length"]');
		const errorEl = box.querySelector('[data-role="error"]');
		intervalInput.focus();
		intervalInput.select();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => {
			const interval = parseFloat(intervalInput.value);
			const profileLength = parseFloat(lengthInput.value);
			if(!isFinite(interval) || interval <= 0 || !isFinite(profileLength) || profileLength <= 0){
				errorEl.textContent = t('Angiv to positive tal.');
				return;
			}
			finish({interval, profileLength});
		};

		const onCancelEvent = () => finish(undefined);

		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			// see WallThicknessPrompt.js's own comment: the popup's input is
			// focused, so DigitizationModule's global Escape handler treats
			// this as "editing a value" and skips itself - handled here instead
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}
